import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, Merchants, Uploads } from "../lib/api.js";
import { Button, Banner, COLORS as C } from "../components/UI.jsx";

const DOC_TYPES = [
  { value: "aadhaar",          label: "Aadhaar Card",         required: true,  hint: "Front + back as PDF or image" },
  { value: "pan",              label: "PAN Card",              required: true,  hint: "PDF or clear photo" },
  { value: "gst_certificate",  label: "GST Certificate",       required: false, hint: "Required if GST-registered" },
  { value: "cancelled_cheque", label: "Cancelled Cheque",      required: false, hint: "For COD payouts to bank" },
  { value: "fssai",            label: "FSSAI License",         required: false, hint: "Required for food/dairy products" },
  { value: "shop_license",     label: "Shop & Establishment",  required: false, hint: "Municipal trade license" },
];

export default function KYC() {
  const navigate = useNavigate();
  if (!auth.loggedIn()) { navigate("/login", { replace: true }); return null; }

  const [merchant, setMerchant] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});   // { doc_type: true }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [numbers, setNumbers] = useState({});       // { doc_type: number_string }

  const load = async () => {
    try {
      const tok = auth.get();
      const [mr, dr] = await Promise.all([
        fetch("/api/v1/merchants/me", { headers: { Authorization: `Bearer ${tok}` } }),
        fetch("/api/v1/merchants/me/kyc-documents", { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      if (mr.ok) setMerchant(await mr.json());
      if (dr.ok) setDocs(await dr.json());
    } catch (e) { setError(e.message); }
  };

  useEffect(() => { load(); }, []);

  const uploadedTypes = new Set(docs.map(d => d.document_type));
  const hasRequired = uploadedTypes.has("aadhaar") && uploadedTypes.has("pan");

  const handleFileSelect = async (docType, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [docType]: true }));
    setError(null);
    try {
      const { url } = await Uploads.file(file);
      const result = await Merchants.submitKycDocument(docType, url, numbers[docType] || null);
      setSuccess(result.message || "Document uploaded successfully.");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(u => ({ ...u, [docType]: false }));
    }
  };

  const status = merchant?.status;

  return (
    <div style={{ minHeight: "100vh", background: C.cream, fontFamily: "-apple-system, system-ui, Inter, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.line}`, padding: "16px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: C.red, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontWeight: 800 }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy }}>Shiftswift</div>
          </Link>
          <Link to="/dashboard" style={{ fontSize: 13, color: C.text2, textDecoration: "none", fontWeight: 600 }}>← Back to dashboard</Link>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "32px 24px 60px" }}>
        {/* Header */}
        <div style={{ fontSize: 11, color: C.red, letterSpacing: 1.5, fontWeight: 700, marginBottom: 8 }}>KYC VERIFICATION</div>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.6 }}>
          Verify your identity
        </h1>
        <p style={{ fontSize: 14, color: C.text2, marginTop: 10, marginBottom: 24, lineHeight: 1.6 }}>
          We're required to verify your identity before you can book shipments. Upload clear copies of your Aadhaar and PAN — that's all that's required.
        </p>

        {/* Status banner */}
        {status === "active" && (
          <Banner kind="success">
            Your account is active. You can book shipments.{" "}
            <Link to="/ship" style={{ color: C.successText, fontWeight: 700 }}>Book now →</Link>
          </Banner>
        )}
        {status === "kyc_submitted" && (
          <Banner kind="info">
            KYC under review — we'll activate your account within 24 hours. We'll email you at {merchant?.email}.
          </Banner>
        )}
        {status === "pending_kyc" && !hasRequired && (
          <Banner kind="info">
            Upload your Aadhaar and PAN to submit KYC. Your account will be activated within 24 hours of submission.
          </Banner>
        )}

        {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}
        {success && <Banner kind="success" onDismiss={() => setSuccess(null)}>{success}</Banner>}

        {/* Document cards */}
        <div style={{ display: "grid", gap: 12 }}>
          {DOC_TYPES.map(dt => {
            const uploaded = docs.find(d => d.document_type === dt.value);
            const busy = uploading[dt.value];
            return (
              <DocCard
                key={dt.value}
                docType={dt}
                uploaded={uploaded}
                busy={busy}
                disabled={status === "active" || status === "kyc_submitted"}
                number={numbers[dt.value] || ""}
                onNumberChange={(v) => setNumbers(n => ({ ...n, [dt.value]: v }))}
                onFile={(f) => handleFileSelect(dt.value, f)}
              />
            );
          })}
        </div>

        {/* Submit notice */}
        {status === "pending_kyc" && (
          <div style={{ marginTop: 20, padding: 16, background: C.white, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 13, color: C.text2, lineHeight: 1.6 }}>
            {hasRequired ? (
              <><strong style={{ color: C.green }}>✓ KYC submitted.</strong> We'll review and activate your account within 24 hours. No further action needed.</>
            ) : (
              <>Upload Aadhaar and PAN to complete KYC. GST, FSSAI and other documents are optional but speed up future category approvals.</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DocCard({ docType, uploaded, busy, disabled, number, onNumberChange, onFile }) {
  const fileRef = useRef(null);

  return (
    <div style={{
      background: C.white, borderRadius: 10, padding: 20,
      border: `1px solid ${uploaded ? C.green : C.line}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{docType.label}</span>
            {docType.required && (
              <span style={{ fontSize: 10, background: C.red, color: C.white, padding: "2px 6px", borderRadius: 4, fontWeight: 700, letterSpacing: 0.5 }}>REQUIRED</span>
            )}
            {uploaded && (
              <span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>✓ Uploaded</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.text3 }}>{docType.hint}</div>
          {uploaded && (
            <div style={{ fontSize: 11, color: C.text4, marginTop: 4 }}>
              Uploaded {uploaded.uploaded_at ? new Date(uploaded.uploaded_at).toLocaleDateString("en-IN") : ""}
              {uploaded.document_number ? ` · ${uploaded.document_number}` : ""}
            </div>
          )}
        </div>

        {!disabled && (
          <div style={{ flexShrink: 0 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              style={{ display: "none" }}
              onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              style={{
                background: uploaded ? "transparent" : C.red,
                color: uploaded ? C.text2 : C.white,
                border: uploaded ? `1px solid ${C.border}` : "none",
                padding: "8px 14px", borderRadius: 6,
                fontSize: 12, fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? "Uploading…" : uploaded ? "Replace" : "Upload"}
            </button>
          </div>
        )}
      </div>

      {/* Optional document number field */}
      {!disabled && !uploaded && (docType.value === "aadhaar" || docType.value === "pan" || docType.value === "gst_certificate") && (
        <div style={{ marginTop: 12 }}>
          <input
            type="text"
            value={number}
            onChange={(e) => onNumberChange(e.target.value)}
            placeholder={
              docType.value === "aadhaar" ? "Aadhaar number (optional)" :
              docType.value === "pan" ? "PAN number (optional)" :
              "GSTIN (optional)"
            }
            style={{
              width: "100%", padding: "9px 12px",
              border: `1px solid ${C.border}`, borderRadius: 6,
              fontSize: 13, boxSizing: "border-box",
              background: C.cream, fontFamily: "inherit",
              outline: "none",
            }}
            onFocus={(e) => { e.target.style.borderColor = C.red; e.target.style.background = C.white; }}
            onBlur={(e) => { e.target.style.borderColor = C.border; e.target.style.background = C.cream; }}
          />
        </div>
      )}
    </div>
  );
}
